import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, CheckCircle, XCircle, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import SurveyModal from '../components/SurveyModal';
import SurveyResultsModal from '../components/SurveyResultsModal';
import { surveyAPI } from '../services/api';

interface Survey {
  id: number;
  title: string;
  description?: string | null;
  is_active: boolean;
  is_onboarding: boolean;
  questions_count?: number;
  responses_count?: number;
  created_at?: string;
  updated_at?: string;
}

const SurveysPage: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/surveys');
      if (response.data.success) {
        setSurveys(response.data.surveys || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки опросов');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSurvey(null);
    setShowModal(true);
  };

  const handleEdit = (survey: Survey) => {
    setEditingSurvey(survey);
    setShowModal(true);
  };

  const handleDelete = async (surveyId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот опрос?')) {
      return;
    }

    try {
      await api.delete(`/surveys/${surveyId}`);
      toast.success('Опрос удален');
      fetchSurveys();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка удаления опроса');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingSurvey(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingSurvey(null);
    fetchSurveys();
  };

  const handleViewResults = (survey: Survey) => {
    setSelectedSurvey(survey);
    setShowResultsModal(true);
  };

  const handleDuplicate = async (surveyId: number) => {
    try {
      const response = await surveyAPI.duplicateSurvey(surveyId);
      if (response.success) {
        toast.success('Опрос успешно продублирован');
        fetchSurveys();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка дублирования опроса');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Опросы</h1>
          <p className="text-gray-600 mt-2">Управление опросами для онбординга и других целей</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Создать опрос</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Описание
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Вопросов
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ответов
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Онбординг
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {surveys.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Нет опросов. Создайте первый опрос.
                </td>
              </tr>
            ) : (
              surveys.map((survey) => (
                <tr key={survey.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{survey.title}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      {survey.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{survey.questions_count || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{survey.responses_count || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {survey.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Активен
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <XCircle className="h-3 w-3 mr-1" />
                        Неактивен
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {survey.is_onboarding ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Да
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">Нет</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleViewResults(survey)}
                        className="text-green-600 hover:text-green-900"
                        title="Просмотр ответов"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(survey.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Дублировать"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(survey)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Редактировать"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(survey.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Удалить"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <SurveyModal
          survey={editingSurvey}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showResultsModal && selectedSurvey && (
        <SurveyResultsModal
          surveyId={selectedSurvey.id}
          surveyTitle={selectedSurvey.title}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedSurvey(null);
          }}
        />
      )}
    </div>
  );
};

export default SurveysPage;

